import { useState } from 'react';
import { useEvent } from '../api/events';
import type { TransactionRow } from '../api/reports';
import {
  useEmailReceipt,
  useReleaseTransactionInventory,
  useTransactionItems,
} from '../api/transactions';
import { ApiError } from '../api/client';
import { Modal } from '../components/Modal';
import { Money } from '../components/Money';

export interface TransactionDrawerProps {
  eventId: string;
  txn: TransactionRow;
  onClose: () => void;
}

/**
 * Build a Stripe Dashboard deep link for a PaymentIntent. Account-prefixed
 * form is used when we have the canonical acct_xxx, otherwise the account-
 * less form (Stripe sends the operator to whichever account their browser
 * session is on). Live URLs drop the /test/ segment.
 */
function stripeDashboardUrl(args: {
  paymentIntentId: string;
  acctId: string | null;
  mode: 'test' | 'live' | null;
}): string {
  const modeSeg = args.mode === 'live' ? '' : '/test';
  const acctSeg = args.acctId ? `/${args.acctId}` : '';
  return `https://dashboard.stripe.com${acctSeg}${modeSeg}/payments/${args.paymentIntentId}`;
}

const STATUS_PILL: Record<TransactionRow['status'], string> = {
  pending: 'pill-draft',
  succeeded: 'pill-paid',
  canceled: 'pill-declined',
  voided: 'pill-declined',
};

export function TransactionDrawer({ eventId, txn, onClose }: TransactionDrawerProps) {
  const release = useReleaseTransactionInventory(eventId);
  const emailReceipt = useEmailReceipt();
  const event = useEvent(eventId);
  const items = useTransactionItems(txn.id);
  const [released, setReleased] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string>('');
  const [receiptEmail, setReceiptEmail] = useState<string>('');
  const [receiptSentTo, setReceiptSentTo] = useState<string | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const refunded = txn.refundedAmountCents > 0;
  const canRelease = txn.status !== 'succeeded' || refunded;
  // Receipts are Stripe-only and only meaningful on a succeeded card sale.
  // Cash rows are hidden entirely (per the operator's request); the
  // "full receipt functionality" with OEG-branded HTML is parked for later.
  const canEmailReceipt = txn.tenderType !== 'cash' && txn.status === 'succeeded';

  const onRelease = async () => {
    setError(null);
    try {
      const r = await release.mutateAsync({
        transactionId: txn.id,
        note: note || undefined,
      });
      setReleased(r.unitsReleased);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Try again.');
      }
    }
  };

  const onSendReceipt = async () => {
    setReceiptError(null);
    try {
      const r = await emailReceipt.mutateAsync({
        transactionId: txn.id,
        email: receiptEmail,
      });
      setReceiptSentTo(r.receiptEmail);
    } catch (err) {
      if (err instanceof ApiError) {
        setReceiptError(err.message);
      } else {
        setReceiptError('Something went wrong. Try again.');
      }
    }
  };

  return (
    <Modal title="Transaction" sub={txn.id.slice(0, 8)} width={520} onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        <Row label="Status">
          <span className={`pill ${STATUS_PILL[txn.status]}`}>{txn.status}</span>
          {refunded && <span className="pill pill-refunded">refunded</span>}
          {txn.disputedAt && <span className="pill pill-declined">disputed</span>}
        </Row>
        <Row label="Amount">
          <Money value={txn.amountCents / 100} />{' '}
          <span style={{ color: 'var(--fg-3)' }}>
            (net <Money value={txn.netCents / 100} />)
          </span>
        </Row>
        <Row label="When">
          {txn.succeededAt
            ? new Date(txn.succeededAt).toLocaleString()
            : new Date(txn.createdAt).toLocaleString()}
        </Row>
        <Row label="Tender">
          {txn.tenderType === 'card_present' ? 'Tap-to-pay' : 'Keyed'}
          {txn.cardBrand && txn.cardLast4 && (
            <span style={{ color: 'var(--fg-3)' }}>
              {' '}
              · {txn.cardBrand} ••{txn.cardLast4}
            </span>
          )}
        </Row>
        {txn.description && <Row label="Description">{txn.description}</Row>}
        {txn.deviceLabel && <Row label="Device">{txn.deviceLabel}</Row>}
        {txn.stripePaymentIntentId && (
          <Row label="Stripe PI">
            <a
              href={stripeDashboardUrl({
                paymentIntentId: txn.stripePaymentIntentId,
                acctId: event.data?.stripeAccountStripeId ?? null,
                mode: event.data?.stripeMode ?? null,
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="mono"
              style={{ color: 'var(--oeg-purple)', textDecoration: 'underline' }}
            >
              {txn.stripePaymentIntentId}
            </a>
          </Row>
        )}

        {/* Line items. Refund allocation per line is proportional to its
            share of the transaction total — surfaced so an operator can see
            "this $X refund roughly came off these lines". */}
        <div style={{ borderTop: '1px solid var(--border-1)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Items</div>
          {items.isLoading && <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>Loading…</div>}
          {items.isError && (
            <div className="form-error" style={{ fontSize: 12 }}>
              Couldn't load items.
            </div>
          )}
          {items.data && items.data.length === 0 && (
            <div style={{ color: 'var(--fg-3)', fontSize: 12 }}>No line items.</div>
          )}
          {items.data && items.data.length > 0 && (
            <table className="tbl" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Unit</th>
                  <th className="num">Total</th>
                  {refunded && <th className="num">Refund</th>}
                </tr>
              </thead>
              <tbody>
                {items.data.map((it) => (
                  <tr key={it.id}>
                    <td>
                      <div>{it.productName ?? it.name}</div>
                      {it.productSku && (
                        <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
                          {it.productSku}
                        </div>
                      )}
                    </td>
                    <td className="num">{it.quantity}</td>
                    <td className="num">
                      <Money value={it.unitPriceCents / 100} />
                    </td>
                    <td className="num">
                      <Money value={it.lineTotalCents / 100} />
                    </td>
                    {refunded && (
                      <td className="num">
                        {it.allocatedRefundCents > 0 ? (
                          <Money value={it.allocatedRefundCents / 100} />
                        ) : (
                          <span style={{ color: 'var(--fg-3)' }}>—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {canEmailReceipt && (
          <div
            style={{
              borderTop: '1px solid var(--border-1)',
              paddingTop: 12,
              marginTop: 8,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Email receipt
            </div>
            <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 8 }}>
              Stripe sends their branded receipt to the customer's email.
              Re-sending overwrites the prior recipient.
            </div>
            <input
              type="email"
              placeholder="customer@example.com"
              maxLength={320}
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.target.value)}
              style={{ width: '100%' }}
              disabled={emailReceipt.isPending}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void onSendReceipt()}
                disabled={emailReceipt.isPending || receiptEmail.length === 0}
              >
                {emailReceipt.isPending ? 'Sending…' : 'Send receipt'}
              </button>
              {receiptSentTo && (
                <span style={{ alignSelf: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                  Sent to {receiptSentTo}.
                </span>
              )}
            </div>
            {receiptError && <div className="form-error">{receiptError}</div>}
          </div>
        )}

        {canRelease && (
          <div
            style={{
              borderTop: '1px solid var(--border-1)',
              paddingTop: 12,
              marginTop: 8,
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
              Release inventory
            </div>
            <div style={{ color: 'var(--fg-3)', fontSize: 12, marginBottom: 8 }}>
              Returns tracked-product units from this transaction back to stock.
              Idempotent — running twice releases nothing the second time.
            </div>
            <input
              type="text"
              placeholder="Note (optional)"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void onRelease()}
                disabled={release.isPending}
              >
                {release.isPending ? 'Releasing…' : 'Release inventory'}
              </button>
              {released !== null && (
                <span style={{ alignSelf: 'center', color: 'var(--fg-3)', fontSize: 12 }}>
                  Released {released} unit{released === 1 ? '' : 's'}.
                </span>
              )}
            </div>
            {error && <div className="form-error">{error}</div>}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems: 'center' }}>
      <div style={{ color: 'var(--fg-3)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}
